import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

function PriorityBadge({ priority }) {
  if (!priority) return null;
  const palette = {
    Primary:   'bg-emerald-50 text-emerald-700 border-emerald-200',
    Secondary: 'bg-amber-50 text-amber-700 border-amber-200',
    Tertiary:  'bg-gray-100 text-gray-700 border-gray-200',
  }[priority] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${palette}`}>
      {priority}
    </span>
  );
}

function TypeBadge({ type }) {
  if (!type) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
      {type}
    </span>
  );
}

export default function SuperadminSourcesIndex({
  sources = [],
  filters = {},
  facets = {},
  total_count = 0,
}) {
  const [query, setQuery] = useState(filters.q || '');
  const [sourceType, setSourceType] = useState(filters.source_type || '');
  const [priorityLevel, setPriorityLevel] = useState(filters.priority_level || '');

  const applyFilters = (overrides = {}) => {
    router.get('/superadmin/sources', {
      q: overrides.q ?? query,
      source_type: overrides.source_type ?? sourceType,
      priority_level: overrides.priority_level ?? priorityLevel,
    }, { preserveState: true, preserveScroll: true });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const handleClear = () => {
    setQuery('');
    setSourceType('');
    setPriorityLevel('');
    router.get('/superadmin/sources', {}, { preserveState: true, preserveScroll: true });
  };

  const showing = sources.length;
  const hasFilter = (filters.q || filters.source_type || filters.priority_level || '').toString().trim().length > 0;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Superadmin
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Sources</h1>
          </div>
          <Link
            href="/superadmin/sources/new"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            Add source
          </Link>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title, author, or URL…"
                className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <select
              value={sourceType}
              onChange={(e) => { setSourceType(e.target.value); applyFilters({ source_type: e.target.value }); }}
              className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">All types</option>
              {(facets.source_types || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={priorityLevel}
              onChange={(e) => { setPriorityLevel(e.target.value); applyFilters({ priority_level: e.target.value }); }}
              className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">All priorities</option>
              {(facets.priority_levels || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <div className="flex gap-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition cursor-pointer"
              >
                Search
              </button>
              {hasFilter && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition cursor-pointer"
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {hasFilter ? 'Matching sources' : 'All sources'}
            </h2>
            <span className="ml-auto text-sm text-gray-400">
              {showing} of {total_count}
              {showing === 500 && ' (showing first 500)'}
            </span>
          </div>

          {showing === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-12 h-12 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </div>
              <p className="text-gray-600">
                {hasFilter ? 'No sources match those filters.' : 'No sources yet.'}
              </p>
              {hasFilter && (
                <button onClick={handleClear} className="mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-700 cursor-pointer">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {sources.map((source) => (
                <div key={source.id} className="flex flex-col sm:flex-row sm:items-start sm:justify-between py-4 gap-3 first:pt-0 last:pb-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900">{source.title}</p>
                      <PriorityBadge priority={source.priority_level} />
                      <TypeBadge type={source.source_type} />
                      {source.format && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                          {source.format}
                        </span>
                      )}
                      {source.approved_for_quoting === 'Yes' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                          </svg>
                          Quotable
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      {source.author_organization}
                    </div>
                  </div>
                  {source.url_or_document_reference && (
                    <a
                      href={source.url_or_document_reference}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition"
                      title={source.url_or_document_reference}
                    >
                      Open
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
